import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { Typography } from '../Typography';
import { CaretUp, CaretDown } from '../svgs';

// ─── Types ────────────────────────────────────────────────────────────────────

export type LeaguePreviewCardType = 'generic' | 'cash' | 'sponsor';

export interface LeaguePreviewCardProps {
  /** Visual variant: generic (white), cash (gold prize), sponsor (dark navy). */
  type?: LeaguePreviewCardType;
  /** League or leaderboard name. */
  name: string;
  /** Current user rank in this league. */
  rank?: string | number;
  /** Whether rank improved or worsened this gameweek. */
  rankTrend?: 'up' | 'down';
  /** Total teams in the league. */
  teams?: string | number;
  /** Gameweek when the league started, e.g. "GW 20". */
  gwStart?: string;
  /** Name of the league creator / chair. */
  chair?: string;
  /**
   * Left image panel content.
   * Generic: club badge; Cash: rendered automatically; Sponsor: sponsor logo.
   */
  image?: ReactNode;
  /** When true, shows a "Complete" badge next to the View League button. */
  isComplete?: boolean;
  /** Tailwind bg class for the left image panel. Overrides the type default when provided. */
  imagePanelBg?: string;
  cashAmount?: string;
  cashLines?: [string, string];
  cashAmountClass?: string;
  cashSwirlSrc?: string;
  ctaLabel?: string;
  disabled?: boolean;
  onViewLeague?: () => void;
  className?: string;
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

const RankArrow: FC<{ trend: 'up' | 'down' }> = ({ trend }) =>
  trend === 'up' ? (
    <CaretUp className="size-4 shrink-0 text-[#00a651]" aria-label="Rank improved" />
  ) : (
    <CaretDown className="size-4 shrink-0 text-[#eb1701]" aria-label="Rank worsened" />
  );

const StatPair: FC<{
  label: string;
  value: string | number;
  trend?: 'up' | 'down';
  labelClass?: string;
  valueClass?: string;
}> = ({
  label,
  value,
  trend,
  labelClass = 'text-[--color-neutral-900]',
  valueClass = 'text-[--color-neutral-900]',
}) => (
  <div className="flex shrink-0 items-center gap-1 sm:gap-2">
    <Typography
      as="span"
      variant="paragraph010"
      className={twMerge('sm:text-14 sm:leading-20', labelClass)}
    >
      {label}
    </Typography>
    <div className="flex items-center gap-0.5">
      <Typography
        as="span"
        variant="paragraph010"
        className={twMerge('!font-bold sm:text-14 sm:leading-20', valueClass)}
      >
        {value}
      </Typography>
      {trend && <RankArrow trend={trend} />}
    </div>
  </div>
);

/**
 * One line of the cash panel, rendered as SVG text stretched to fill the panel's
 * full width. Because every line shares the same `textLength`, they all end up the
 * same width with aligned left/right edges regardless of character count — the
 * font-size adjusts per line to fit, which is what gives the justified block look.
 * `height` sets the line's relative size (taller viewBox => bigger line).
 */
const CashLine: FC<{ text: string; height: number; className?: string }> = ({
  text,
  height,
  className,
}) => (
  <svg
    viewBox={`0 0 100 ${height}`}
    preserveAspectRatio="xMidYMid meet"
    className={twMerge('block w-full', className)}
    role="presentation"
  >
    <text
      x="50"
      y={height / 2}
      textAnchor="middle"
      dominantBaseline="central"
      textLength="76"
      lengthAdjust="spacingAndGlyphs"
      fontSize={height}
      fill="currentColor"
    >
      {text}
    </text>
  </svg>
);

const CashPanelContent: FC<{
  amount: string;
  lines: [string, string];
  amountClass: string;
  swirlSrc?: string;
}> = ({ amount, lines, amountClass, swirlSrc }) => (
  <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
    {swirlSrc && (
      <img
        src={swirlSrc}
        alt=""
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      />
    )}
    <div className="relative h-full flex flex-col justify-center select-none sm:px-8 font-[TheSun] font-[900]">
      <CashLine text={amount} height={26} className={amountClass} />
      <CashLine text={lines[0]} height={34} className="text-white" />
      <CashLine text={lines[1]} height={26} className="text-white" />
    </div>
  </div>
);

// ─── Image panel ──────────────────────────────────────────────────────────────

const panelBg: Record<LeaguePreviewCardType, string> = {
  generic: 'bg-[--color-yellow-100]',
  cash: 'bg-[--color-gold-200]',
  sponsor: 'bg-[#242651]',
};

const panelBorder: Record<LeaguePreviewCardType, string> = {
  generic: 'border-r border-[--color-grey-200]',
  cash: 'border-r border-[--color-grey-200]',
  sponsor: 'border-r border-[#4c5094]',
};

const ImagePanel: FC<{
  type: LeaguePreviewCardType;
  image?: ReactNode;
  bg?: string;
  cashAmount: string;
  cashLines: [string, string];
  cashAmountClass: string;
  cashSwirlSrc?: string;
}> = ({ type, image, bg, cashAmount, cashLines, cashAmountClass, cashSwirlSrc }) => (
  <div
    className={twMerge(
      'flex shrink-0 items-center justify-center overflow-hidden',
      'w-[88px] sm:w-[160px]',
      'self-stretch',
      bg ?? panelBg[type],
      panelBorder[type]
    )}
    aria-hidden
  >
    {type === 'cash' ? (
      <CashPanelContent
        amount={cashAmount}
        lines={cashLines}
        amountClass={cashAmountClass}
        swirlSrc={cashSwirlSrc}
      />
    ) : (
      (image ?? null)
    )}
  </div>
);

// ─── Card shell styles ────────────────────────────────────────────────────────

const cardBg: Record<LeaguePreviewCardType, string> = {
  generic: 'bg-white',
  cash: 'bg-[--color-grey-200]',
  sponsor: 'bg-[#242651]',
};

const cardBorder: Record<LeaguePreviewCardType, string> = {
  generic: 'border border-[--color-grey-200]',
  cash: 'border border-[--color-grey-200]',
  sponsor: 'border border-[#4c5094]',
};

// ─── Per-type text colours ────────────────────────────────────────────────────

const titleClass: Record<LeaguePreviewCardType, string> = {
  generic: 'text-[--color-neutral-900]',
  cash: 'text-[--color-neutral-900]',
  sponsor: 'text-[#2efcad]',
};

const labelClass: Record<LeaguePreviewCardType, string> = {
  generic: 'text-[--color-neutral-900]',
  cash: 'text-[--color-neutral-900]',
  sponsor: 'text-white',
};

const viewLeagueClass: Record<LeaguePreviewCardType, string> = {
  generic: 'text-[--color-blue-200]',
  cash: 'text-[--color-blue-200]',
  sponsor: 'text-[#2efcad]',
};

const completeBadgeBg: Record<LeaguePreviewCardType, string> = {
  generic: 'bg-[--color-green-100]',
  cash: 'bg-[--color-green-100]',
  sponsor: 'bg-[#2efcad]',
};

const completeBadgeText: Record<LeaguePreviewCardType, string> = {
  generic: 'text-[--color-neutral-900]',
  cash: 'text-[--color-neutral-900]',
  sponsor: 'text-[#242651]',
};

// ─── Main component ───────────────────────────────────────────────────────────

export const LeaguePreviewCard: FC<LeaguePreviewCardProps> = ({
  type = 'generic',
  name,
  rank,
  rankTrend,
  teams,
  gwStart,
  chair,
  image,
  isComplete,
  imagePanelBg,
  cashAmount = '£60K',
  cashLines = ['TOP', 'PRIZE'],
  cashAmountClass = 'text-[#030033]',
  cashSwirlSrc,
  ctaLabel = 'View League',
  disabled = false,
  onViewLeague,
  className,
}) => {
  const lc = labelClass[type];

  const titleEl = (
    <Typography
      as="p"
      variant="headline025"
      className={twMerge('w-full truncate !font-[900] sm:text-24 sm:leading-28', titleClass[type])}
    >
      {name}
    </Typography>
  );

  // Stats wrap: 4px row gap; 16px column gap (mobile) / 20px (desktop).
  const statsEl = (
    <div className="flex w-full flex-wrap content-start items-start gap-x-4 gap-y-1 sm:gap-x-5">
      {rank !== undefined && (
        <StatPair label="Rank:" value={rank} trend={rankTrend} labelClass={lc} valueClass={lc} />
      )}
      {teams !== undefined && (
        <StatPair label="Teams:" value={teams} labelClass={lc} valueClass={lc} />
      )}
      {gwStart !== undefined && (
        <StatPair label="Start:" value={gwStart} labelClass={lc} valueClass={lc} />
      )}
      {chair !== undefined && (
        <StatPair label="Chair:" value={chair} labelClass={lc} valueClass={lc} />
      )}
    </div>
  );

  const buttonEl = (
    <button
      type="button"
      onClick={disabled ? undefined : onViewLeague}
      disabled={disabled}
      className="shrink-0 rounded-[--corner-xs] py-[3px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-default"
    >
      <Typography
        as="span"
        variant="button020"
        className={disabled ? 'text-[--color-neutral-500]' : viewLeagueClass[type]}
      >
        {ctaLabel}
      </Typography>
    </button>
  );

  const completeEl = isComplete ? (
    <span
      className={twMerge(
        'shrink-0 rounded-[--corner-xs] px-2 py-0.5',
        completeBadgeBg[type],
        completeBadgeText[type]
      )}
    >
      <Typography as="span" variant="label020" className="!font-[900]">
        Complete
      </Typography>
    </span>
  ) : null;

  return (
    <div
      className={twMerge(
        // Fixed height: 132px on mobile, 100px on desktop (sm+). No intrinsic sizing.
        'flex h-[132px] w-full overflow-hidden rounded-[--corner-md] sm:h-[100px]',
        cardBg[type],
        cardBorder[type],
        className
      )}
    >
      <ImagePanel
        type={type}
        image={image}
        bg={imagePanelBg}
        cashAmount={cashAmount}
        cashLines={cashLines}
        cashAmountClass={cashAmountClass}
        cashSwirlSrc={cashSwirlSrc}
      />

      {/* Info panel: 16px padding (mobile) / 20px (desktop), vertically centred. */}
      <div className="flex min-w-0 flex-1 flex-col justify-center p-4 sm:p-5">
        {/*
          Mobile layout (Figma node 8154:451323): a single column —
          name → stats → View League row. 8px name↔stats, 12px stats↔button.
        */}
        <div className="flex w-full flex-col gap-3 sm:hidden">
          <div className="flex w-full flex-col items-start gap-2">
            {titleEl}
            {statsEl}
          </div>
          <div className="flex w-full items-center gap-3">
            {buttonEl}
            {completeEl}
          </div>
        </div>

        {/*
          Desktop layout (Figma node 8159:461553): two full-width rows, 12px apart.
          Row 1 — name (flex-1, truncates) with the Complete badge pinned right.
          Row 2 — stats (flex-1, wraps) with View League pinned right. 16px gap
          between each row's left block and its right-pinned element.
        */}
        <div className="hidden w-full flex-col gap-3 sm:flex">
          <div className="flex w-full items-center gap-4">
            <div className="min-w-0 flex-1">{titleEl}</div>
            {completeEl}
          </div>
          <div className="flex w-full items-center gap-4">
            <div className="min-w-0 flex-1">{statsEl}</div>
            {buttonEl}
          </div>
        </div>
      </div>
    </div>
  );
};
